import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { ModulePrice } from '@/types/marketplace';

export interface ModulePriceFormData {
  code: string;
  name: string;
  description?: string | null;
  price: number;
  is_active: boolean;
  icon?: string | null;
  display_order?: number | null;
}

export const useModulePrices = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: modulePrices = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['module-prices-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_prices')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ModulePrice[];
    }
  });

  const addModuleMutation = useMutation({
    mutationFn: async (moduleData: ModulePriceFormData) => {
      const { data, error } = await supabase
        .from('module_prices')
        .insert([moduleData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-prices-admin'] });
      queryClient.invalidateQueries({ queryKey: ['module-prices'] });
      toast({ title: "Módulo criado", description: "Módulo adicionado ao catálogo com sucesso." });
    },
    onError: (error: any) => {
      console.error('Error creating module:', error);
      toast({ title: "Erro ao criar módulo", description: "Não foi possível criar o módulo.", variant: "destructive" });
    }
  });

  const updateModuleMutation = useMutation({
    mutationFn: async ({ id, data: moduleData }: { id: string; data: Partial<ModulePriceFormData> }) => {
      const { data, error } = await supabase
        .from('module_prices')
        .update(moduleData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-prices-admin'] });
      queryClient.invalidateQueries({ queryKey: ['module-prices'] });
      toast({ title: "Módulo atualizado", description: "Módulo atualizado com sucesso." });
    },
    onError: (error: any) => {
      console.error('Error updating module:', error);
      toast({ title: "Erro ao atualizar módulo", description: "Não foi possível atualizar o módulo.", variant: "destructive" });
    }
  });

  const deleteModuleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('module_prices')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-prices-admin'] });
      queryClient.invalidateQueries({ queryKey: ['module-prices'] });
      toast({ title: "Módulo excluído", description: "Módulo removido do catálogo." });
    },
    onError: (error: any) => {
      console.error('Error deleting module:', error);
      toast({ title: "Erro ao excluir módulo", description: "Não foi possível excluir o módulo.", variant: "destructive" });
    }
  });

  return {
    modulePrices,
    isLoading,
    error,
    addModule: addModuleMutation.mutate,
    updateModule: updateModuleMutation.mutate,
    deleteModule: deleteModuleMutation.mutate,
    isAdding: addModuleMutation.isPending,
    isUpdating: updateModuleMutation.isPending,
    isDeleting: deleteModuleMutation.isPending
  };
};
